import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import Label from "../../common/Label";
import { fetchPricingRule } from "../../service";
import { formatMoney } from "../../../../utils";
import { getSettings } from "../../../../storage";

const COLORS = {
    border: "#f87678ff",
    text: "#e30808ff",
};

const PricingRules = ({
    isHome = false,
    subSkill,
    range,
    calculateFutureTime,
}) => {
    const stepFlowIsEnable =
        getSettings()?.customer_web_job_step_flow_is_enable;

    const [loading, setLoading] = useState(true);
    const [pricingData, setPricingData] = useState({
        basePrice: 0,
        rules: [],
    });

    const subSkillId = subSkill?.sub_skill_id || 0;

    /* -------------------- Fetch pricing rules -------------------- */
    useEffect(() => {
        let active = true;

        const loadPricingRules = async () => {
            try {
                setLoading(true);

                const startTime = calculateFutureTime(range?.[0]);
                const endTime = calculateFutureTime(range?.[1]);

                const res = await fetchPricingRule({
                    startTime,
                    endTime,
                    subSkillId,
                });

                if (!active) return;

                if (res?.status === "Success") {
                    setPricingData({
                        basePrice: res?.data?.basePrice || 0,
                        rules: res?.data?.rules || [],
                    });
                } else {
                    setPricingData({
                        basePrice: 0,
                        rules: [],
                    });
                }
            } catch (error) {
                if (active) {
                    setPricingData({
                        basePrice: 0,
                        rules: [],
                    });
                }
            } finally {
                active && setLoading(false);
            }
        };

        loadPricingRules();

        return () => {
            active = false;
        };
    }, [range, subSkillId, calculateFutureTime]);

    const { basePrice, rules } = pricingData;

    /* -------------------- Rule messages -------------------- */
    const ruleMessages = useMemo(() => {
        return rules
            .map(({ rule }) => rule?.rule_message)
            .filter(Boolean);
    }, [rules]);

    /* -------------------- Breakdown text -------------------- */
    const breakdownText = useMemo(() => {
        if (!rules.length) return "";

        const ruleText = rules
            .map(({ rule }) => {
                if (!rule?.rule_name) return null;

                return `${rule.rule_name} +${rule.rule_percentage}%`;
            })
            .filter(Boolean)
            .join(" × ");

        return basePrice > 0
            ? `Base ${formatMoney(basePrice)} × ${ruleText}`
            : ruleText;
    }, [rules, basePrice]);

    /* -------------------- Early exits -------------------- */
    if (loading) return null;

    if (!rules.length) {
        return isHome ? <div className="mt-1" /> : null;
    }

    /* -------------------- HOME STEP FLOW UI -------------------- */
    if (isHome && stepFlowIsEnable) {
        return (
            <div className="mt-2">
                <Label text="Surcharges" />

                <div
                    className="bg-white rounded-4 p-3"
                    style={{
                        borderRadius: "10px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    }}
                >
                    {rules.map(({ rule }, index) => (
                        <div key={index}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <p className="mb-0 text-sm fw-medium me-2">
                                        {rule?.rule_name}
                                    </p>

                                    {!!rule?.rule_message && (
                                        <Tooltip title={rule.rule_message}>
                                            <InfoOutlinedIcon
                                                sx={{ fontSize: 15 }}
                                            />
                                        </Tooltip>
                                    )}
                                </div>

                                <p
                                    className="mb-0 text-sm fw-semibold"
                                    style={{ color: "#555" }}
                                >
                                    +{rule?.rule_percentage}%
                                </p>
                            </div>

                            {index !== rules.length - 1 && (
                                <hr
                                    className="my-2"
                                    style={{
                                        backgroundColor: "#918d8d",
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    /* -------------------- HOME NORMAL UI -------------------- */
    if (isHome) {
        return (
            <div
                className="card mb-1"
                style={{ borderColor: COLORS.border }}
            >
                <div className="card-header p-2 ps-3">
                    <div className="d-flex justify-content-between">
                        <div>
                            <p className="text-xs mb-0 text-capitalize text-danger">
                                Breakdown
                            </p>

                            <p className="mb-0 text-sm text-danger fw-bold">
                                {breakdownText}
                            </p>
                        </div>

                        <div className="icon icon-md icon-shape bg-gradient-danger text-center border-radius-lg">
                            <i className="material-symbols-rounded opacity-10">
                                credit_card_clock
                            </i>
                        </div>
                    </div>
                </div>

                <hr className="dark horizontal my-0" />

                <div className="card-footer p-2 ps-3">
                    {ruleMessages.map((msg, idx) => (
                        <p
                            key={idx}
                            className={`mb-0 text-xs text-danger ${
                                idx > 0 ? "mt-2" : ""
                            }`}
                        >
                            {msg}
                        </p>
                    ))}
                </div>
            </div>
        );
    }

    /* -------------------- GENERAL UI -------------------- */
    return (
        <div className="mt-4">
            <div
                className="card card-frame mx-3 mb-3"
                style={{
                    border: `1px solid ${COLORS.border}`,
                }}
            >
                <div className="card-body">
                    <Label
                        text="Pricing rules"
                        textColor={COLORS.text}
                    />

                    <p
                        className="mt-1 mb-3 text-sm"
                        style={{ color: COLORS.text }}
                    >
                        {ruleMessages.join(", ")}
                    </p>

                    <Label
                        text="Breakdown"
                        textColor={COLORS.text}
                    />

                    <p
                        className="mt-1 mb-0 text-sm"
                        style={{ color: COLORS.text }}
                    >
                        {breakdownText}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PricingRules;